<template>
  <div v-if="requests && requests.length > 0">
    <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
      <h2 class="font-weight-bolder m-0">
        Solicitudes de ausencias
      </h2>
      <div class="d-flex align-items-center">
        <span
          class="ml-1 cursor-pointer"
          @click="visibleFilter = !visibleFilter"
        >
          <feather-icon
            icon="FilterIcon"
            size="20"
          />
        </span>
      </div>
    </div>
    <b-collapse
      id="collapse-filter"
      v-model="visibleFilter"
      class="mt-2"
    >
      <div class="px-2 mb-2">
        <b-row>
          <b-col sm="6">
            <b-form-group label="Filtrar por tipo" label-for="type-select" class="mb-0">
              <v-select
                v-model="selectedType"
                label="name"
                :filterable="true"
                :searchable="true"
                :options="requestTypes"
                :placeholder="'Todos los tipos'"
                @input="handleTypeChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </b-collapse>
    <b-card no-body>
      <b-table
        hover
        responsive
        :items="requests"
        :fields="fields"
        :busy="loading"
        class="table-white pt-1"
      >
        <template #cell(type)="data">
          <b-badge variant="light-primary">
            {{ data.item.type.name }}
          </b-badge>
        </template>

        <template #cell(requested_days)="data">
          <span v-if="data.item.type.name !== 'Horas de ausencia'">
            {{ data.item.requested_days }} días
          </span>
          <span v-else>
            {{ data.item.requested_days }}
          </span>
        </template>

        <template #cell(dates)="data">
          <div class="text-nowrap">
            <span class="text-muted">{{ formatDate(data.item.start_date) }}</span>
            <span class="mx-1">-</span>
            <span class="text-muted">{{ formatDate(data.item.end_date) }}</span>
          </div>
        </template>

        <template #cell(comment)="data">
          <span v-if="data.item.comment" class="text-muted">
            {{ data.item.comment }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(status)="data">
          <span :class="`badge badge-${getStatusBadgeClass(data.item.status)}`">
            {{ formatStatus(data.item.status) }}
          </span>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <span style="width: 24px; display: inline-flex; justify-content: center;">
              <span
                class="cursor-pointer"
                :class="data.item.files && data.item.files.length > 0 ? 'text-success' : 'text-muted'"
                :title="data.item.files && data.item.files.length > 0 ? 'Ver justificantes' : 'Añadir justificante'"
                @click="openJustificanteModal(data.item)"
              >
                <feather-icon icon="PaperclipIcon" size="18" />
              </span>
            </span>
            <span style="width: 24px; display: inline-flex; justify-content: center;">
              <span
                v-if="isAdminOrSuperAdmin && isRejectableStatus(data.item.status) && isFutureAbsence(data.item.start_date)"
                class="cursor-pointer text-danger"
                title="Rechazar ausencia"
                @click="confirmReject(data.item)"
              >
                <feather-icon icon="XCircleIcon" size="18" />
              </span>
            </span>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-4">
            <p class="text-muted">No hay solicitudes de ausencias.</p>
          </div>
        </template>

        <template #table-busy>
          <div class="text-center text-muted my-3">
            <b-spinner small class="mr-2" />
            <span>Cargando solicitudes...</span>
          </div>
        </template>
      </b-table>
    </b-card>

    <absence-document-modal
      v-model="showJustificanteModal"
      :absence-id="justificanteRequest ? justificanteRequest.id : null"
      :files="justificanteRequest ? justificanteRequest.files : []"
      @updated="justificanteRequest = { ...justificanteRequest, files: $event }"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BRow, BCol, BTable, BSpinner, BCard, BFormGroup, BCollapse, BBadge } from 'bootstrap-vue'
import vSelect from 'vue-select'
import AbsenceDocumentModal from '@/components/absences/AbsenceDocumentModal.vue'
import { getUserRole } from '@/auth/utils'

export default {
  name: 'UserAbsencesRequests',
  components: {
    BRow,
    BCol,
    BTable,
    BSpinner,
    BCard,
    BFormGroup,
    BCollapse,
    BBadge,
    vSelect,
    AbsenceDocumentModal,
  },
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      visibleFilter: false,
      selectedType: null,
      showJustificanteModal: false,
      justificanteRequest: null,
      fields: [
        { key: 'type', label: 'Tipo', sortable: false },
        { key: 'requested_days', label: 'Días', sortable: false },
        { key: 'dates', label: 'Fechas', sortable: false },
        { key: 'comment', label: 'Comentario', sortable: false },
        { key: 'status', label: 'Estado', sortable: false },
        { key: 'actions', label: '', sortable: false },
      ],
    }
  },
  computed: {
    ...mapGetters({
      requests: 'absences/getItems',
      requestTypes: 'absences/getRequestTypes',
      currentClient: 'clients/getCurrentClient',
    }),
    isAdminOrSuperAdmin() {
      const role = getUserRole()
      return role === 'admin' || role === 'super_admin'
    },
  },
  mounted() {
    this.fetchRequestTypes()
    this.refreshRequests()
  },
  methods: {
    ...mapActions({
      fetchRequests: 'absences/getListAbsences',
      fetchRequestTypes: 'absences/getRequestTypes',
      approve: 'absences/approve',
    }),
    getStatusBadgeClass(status) {
      const classes = {
        pending: 'warning',
        approved: 'success',
        denied: 'danger',
        cancelled: 'secondary',
      }
      return classes[status] || 'secondary'
    },
    formatStatus(status) {
      const statusMap = {
        pending: 'Pendiente',
        approved: 'Aprobada',
        denied: 'Rechazada',
        cancelled: 'Cancelada',
      }
      return statusMap[status] || status
    },
    async refreshRequests() {
      this.loading = true
      try {
        await this.fetchRequests({
          page: 1,
          per_page: 100,
          type_id: this.selectedType ? this.selectedType.id : '',
          user_id: this.userId,
          date_ini: '',
          date_fin: '',
          status: '',
          client_id: this.currentClient && this.currentClient.id ? this.currentClient.id : '',
          department_id: '',
        })
      } finally {
        this.loading = false
      }
    },
    handleTypeChange() {
      this.refreshRequests()
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString()
    },
    isRejectableStatus(status) {
      return status === 'pending' || status === 'approved'
    },
    isFutureAbsence(startDate) {
      if (!startDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return new Date(startDate) >= today
    },
    openJustificanteModal(item) {
      this.justificanteRequest = { ...item, files: [...(item.files || [])] }
      this.showJustificanteModal = true
    },
    confirmReject(item) {
      this.$bvModal.msgBoxConfirm(
        '¿Estás seguro de que deseas rechazar esta solicitud de ausencia?',
        {
          title: 'Rechazar ausencia',
          okVariant: 'danger',
          okTitle: 'Rechazar',
          cancelTitle: 'Cancelar',
          centered: true,
          cancelVariant: 'outline-danger',
        },
      ).then(confirmed => {
        if (confirmed) {
          const formData = new FormData()
          formData.append('status', 'denied')
          formData.append('comment', 'Rechazado por el administrador')
          this.approve({ id: item.id, absence: formData })
        }
      })
    },
  },
}
</script>
